import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button } from '@mui/material';
import PatientPicker from './PatientPicker';
import { Patient } from '../../types/types';
import APIService from '../../services/APIService';
import { useAccessToken } from '@features/user';
import { useEventTracking } from '@services/statsig';

interface AssignNoteDialogProps {
  open: boolean;
  onClose: () => void;
  noteId: string;
  encounterId?: string;
  onAssign: (patient: Patient) => void;
  onAssignError?: () => void;
}

const AssignPatientToNoteDialog: React.FC<AssignNoteDialogProps> = ({
  open,
  onClose,
  noteId,
  encounterId,
  onAssign,
  onAssignError,
}) => {
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(undefined);
  const { getAccessToken } = useAccessToken();
  const { note } = useEventTracking();
  const assignedRef = useRef(false);

  // Track dialog opened when open changes to true
  useEffect(() => {
    if (open) {
      assignedRef.current = false; // Reset on open
      note.patientAssignmentDialogOpened(noteId, {
        encounter_id: encounterId,
      });
    } else if (!assignedRef.current) {
      // Dialog closed without assigning
      note.patientAssignmentCancelled(noteId, {
        encounter_id: encounterId,
      });
    }
  }, [open, noteId, encounterId, note]);

  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  const handleAssign = async () => {
    if (!selectedPatient) return;

    // Mark as assigned before closing
    assignedRef.current = true;

    // Call callback IMMEDIATELY for optimistic update
    onAssign(selectedPatient);

    // Close dialog immediately
    onClose();

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/assign-note',
        accessToken,
        body: {
          patient_id: selectedPatient.patient_id,
          note_id: noteId,
        },
      });

      if (!response.ok) {
        console.error('Failed to assign note to patient');
        // On error, call error handler to invalidate cache and refetch
        onAssignError?.();
      } else {
        // Track successful assignment
        note.patientAssigned(noteId, {
          patient_id: selectedPatient.patient_id,
          encounter_id: encounterId,
        });
      }
    } catch (error) {
      console.error('Error assigning note to patient:', error);
      // On error, call error handler to invalidate cache and refetch
      onAssignError?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Assign Note to Patient</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, minWidth: 300 }}>
          <PatientPicker onPatientSelect={handlePatientSelect} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAssign} disabled={!selectedPatient}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPatientToNoteDialog;
